import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Crosshair, ExternalLink, Link2, MapPin, Search, X } from "lucide-react";

import { api } from "@/lib/api-client";
import { EMIRATE_CENTRE, mapsUrl } from "@/lib/geo";
import { Label } from "./ui";

type Pin = { lat: number; lng: number };

const PIN_ICON = L.divIcon({ className: "", html: '<div class="vsid-pin"></div>', iconSize: [26, 26], iconAnchor: [13, 26] });

/**
 * Site pin for an order. The agent drops it by tapping the map, dragging the
 * marker, using the phone's GPS while standing on site, or pasting a Google
 * Maps share link. The pin is stored as lat/lng, shown as an "Open in Google
 * Maps" link everywhere, and copied onto the project for the team.
 *
 * OpenStreetMap tiles and Nominatim geocoding: no API key, fine for this
 * volume. A Google Places key can replace the address search later.
 */
export function LocationPicker({
  lat,
  lng,
  address,
  emirate,
  disabled,
  onChange,
  onUseAddress,
}: {
  lat: number | null | undefined;
  lng: number | null | undefined;
  address: string;
  emirate?: string;
  disabled: boolean;
  onChange: (pin: Pin | null) => void;
  onUseAddress: (text: string) => void;
}) {
  const pin: Pin | null = typeof lat === "number" && typeof lng === "number" ? { lat, lng } : null;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState("");
  const [suggested, setSuggested] = useState<string | null>(null);
  const mapEl = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const marker = useRef<L.Marker | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // One map per open panel; torn down when it closes.
  useEffect(() => {
    if (!open || !mapEl.current) return;
    const centre: [number, number] = pin ? [pin.lat, pin.lng] : (EMIRATE_CENTRE[emirate ?? ""] ?? EMIRATE_CENTRE.Dubai!);
    const m = L.map(mapEl.current, { zoomControl: true, attributionControl: true }).setView(centre, pin ? 17 : 11);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(m);
    if (!disabled) {
      m.on("click", (e: L.LeafletMouseEvent) => onChangeRef.current({ lat: round(e.latlng.lat), lng: round(e.latlng.lng) }));
    }
    map.current = m;
    setTimeout(() => m.invalidateSize(), 50);
    return () => {
      m.remove();
      map.current = null;
      marker.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the marker in step with the stored pin.
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    if (!pin) {
      marker.current?.remove();
      marker.current = null;
      return;
    }
    if (!marker.current) {
      marker.current = L.marker([pin.lat, pin.lng], { icon: PIN_ICON, draggable: !disabled }).addTo(m);
      marker.current.on("dragend", () => {
        const p = marker.current!.getLatLng();
        onChangeRef.current({ lat: round(p.lat), lng: round(p.lng) });
      });
    } else {
      marker.current.setLatLng([pin.lat, pin.lng]);
    }
    if (!m.getBounds().pad(-0.2).contains([pin.lat, pin.lng])) m.setView([pin.lat, pin.lng], Math.max(m.getZoom(), 16));
  }, [pin?.lat, pin?.lng, disabled, open]);

  // Reverse geocode a fresh pin so the agent can take the address with one tap.
  useEffect(() => {
    setSuggested(null);
    if (!open || !pin || disabled) return;
    const ctl = new AbortController();
    const t = setTimeout(() => {
      fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${pin.lat}&lon=${pin.lng}&zoom=18&accept-language=en`, { signal: ctl.signal })
        .then((r) => (r.ok ? r.json() : null))
        .then((j: { display_name?: string } | null) => {
          const name = j?.display_name?.trim();
          if (name && name !== address.trim()) setSuggested(name);
        })
        .catch(() => undefined);
    }, 600);
    return () => {
      clearTimeout(t);
      ctl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin?.lat, pin?.lng, open]);

  function useGps() {
    if (!navigator.geolocation) {
      setError("No GPS in this browser.");
      return;
    }
    setError(null);
    setBusy("Locating…");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange({ lat: round(pos.coords.latitude), lng: round(pos.coords.longitude) });
        setBusy(null);
      },
      (e) => {
        setError(e.code === e.PERMISSION_DENIED ? "Location permission refused. Allow it in the browser settings." : "Could not get a position.");
        setBusy(null);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  }

  async function findAddress() {
    const q = address.trim();
    if (!q) {
      setError("Type the address first.");
      return;
    }
    setError(null);
    setBusy("Searching…");
    try {
      const qs = new URLSearchParams({ format: "jsonv2", q, limit: "1", countrycodes: "ae", "accept-language": "en" });
      const r = await fetch(`https://nominatim.openstreetmap.org/search?${qs}`);
      const rows = (await r.json()) as Array<{ lat: string; lon: string }>;
      if (!rows[0]) {
        setError("Address not found on the map. Tap the spot or use GPS.");
        return;
      }
      onChange({ lat: round(Number(rows[0].lat)), lng: round(Number(rows[0].lon)) });
    } catch {
      setError("Search failed.");
    } finally {
      setBusy(null);
    }
  }

  async function pasteLink() {
    const url = link.trim();
    if (!url) return;
    setError(null);
    setBusy("Reading link…");
    try {
      const p = await api.resolveMapsLink(url);
      onChange({ lat: round(p.lat), lng: round(p.lng) });
      setLink("");
    } catch (e) {
      let msg = "Could not read that link.";
      if (e instanceof Error) {
        try {
          const j = JSON.parse(e.message) as { message?: string };
          if (typeof j.message === "string") msg = j.message;
        } catch {
          /* not JSON */
        }
      }
      setError(msg);
    } finally {
      setBusy(null);
    }
  }

  const btn = "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[0.6rem] uppercase tracking-[0.12em] transition disabled:opacity-50";
  const gold = `${btn} border-gold/50 bg-gold/10 text-goldbright hover:bg-gold/20`;
  const plain = `${btn} border-white/15 text-ice/70 hover:text-paper`;

  return (
    <div className="sm:col-span-2">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={pin ? plain : gold} onClick={() => setOpen((v) => !v)}>
          <MapPin className="h-3 w-3" /> {pin ? (open ? "Hide map" : "Pinned · edit") : "Pin location"}
        </button>
        {pin && (
          <a href={mapsUrl(pin.lat, pin.lng)} target="_blank" rel="noreferrer" className={`${plain} normal-case tracking-normal`}>
            <ExternalLink className="h-3 w-3" /> Open in Google Maps
          </a>
        )}
        {pin && !disabled && (
          <button type="button" className={`${plain} text-pink/80`} onClick={() => onChange(null)} aria-label="Remove pin">
            <X className="h-3 w-3" /> Remove
          </button>
        )}
      </div>

      {open && (
        <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
          {!disabled && (
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <button type="button" className={gold} disabled={Boolean(busy)} onClick={useGps}>
                <Crosshair className="h-3 w-3" /> {busy === "Locating…" ? busy : "My position"}
              </button>
              <button type="button" className={plain} disabled={Boolean(busy)} onClick={() => void findAddress()}>
                <Search className="h-3 w-3" /> {busy === "Searching…" ? busy : "Find address"}
              </button>
              <span className="text-[0.62rem] text-ice/45">or tap the map · drag the pin</span>
            </div>
          )}
          <div ref={mapEl} className="h-64 w-full overflow-hidden rounded-lg border border-white/10" />
          {!disabled && (
            <div className="mt-2 flex items-center gap-2">
              <Link2 className="h-3.5 w-3.5 shrink-0 text-ice/50" />
              <input
                className="field !py-1.5 text-xs"
                placeholder="Paste a Google Maps link (Share → Copy link)"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void pasteLink();
                  }
                }}
                dir="ltr"
              />
              <button type="button" className={plain} disabled={Boolean(busy) || !link.trim()} onClick={() => void pasteLink()}>
                {busy === "Reading link…" ? busy : "Use"}
              </button>
            </div>
          )}
          {pin && (
            <p className="mt-2 text-[0.62rem] text-ice/50">
              <Label>Pin</Label> {pin.lat.toFixed(6)}, {pin.lng.toFixed(6)}
            </p>
          )}
          {suggested && !disabled && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ice/75">
              <span className="min-w-0 flex-1 truncate" title={suggested}>{suggested}</span>
              <button type="button" className={gold} onClick={() => onUseAddress(suggested)}>
                Use as address
              </button>
            </div>
          )}
          {error && <p className="mt-2 text-xs text-pink">{error}</p>}
        </div>
      )}
    </div>
  );
}

const round = (n: number) => Math.round(n * 1e6) / 1e6;
